import { cn, NonTimezoneSensitiveDate } from '@commenda/utils';
import React, { useRef } from 'react';
import { useScrollGradient } from '../../../hooks/useScrollGradient';
import AgendaDateGroup, { AgendaMode } from './AgendaDateGroup';
import { RbcEvent } from './calendar.types';

interface CalendarAgendaViewProps {
  eventsByDate: Map<string, RbcEvent[]>;
  /** ISO 8601 calendar date (YYYY-MM-DD). */
  currentMonth: string;
  onSelectEvent: (event: RbcEvent) => void;
  mode?: AgendaMode;
  calendarNotGenerated?: boolean;
  viewStart?: string;
  viewEnd?: string;
  header?: React.ReactNode;
  /** Invoked after a successful per-row mutation (extend / request-extension)
   * so the parent can refetch the calendar events. */
  onFilingMutated?: () => void;
  /** Optional overrides for the outer card container (e.g. border radius). */
  className?: string;
}

interface DateGroup {
  key: string;
  events: RbcEvent[];
}

const buildDateGroups = (
  eventsByDate: Map<string, RbcEvent[]>,
  start: NonTimezoneSensitiveDate,
  end: NonTimezoneSensitiveDate
): DateGroup[] => {
  const groups: DateGroup[] = [];
  let cursor = start;
  while (cursor.lte(end)) {
    const key = cursor.toISO8601String();
    const events = eventsByDate.get(key);
    if (events && events.length > 0) {
      groups.push({ key, events });
    }
    cursor = cursor.add(1, 'days');
  }
  return groups;
};

interface EmptyStateProps {
  calendarNotGenerated: boolean;
  mode: AgendaMode;
}

const EmptyState = ({ calendarNotGenerated, mode }: EmptyStateProps) => {
  if (calendarNotGenerated) {
    return (
      <>
        <span className="txt-medium-bold">
          Your compliance calendar isn't set up yet
        </span>
        <span className="txt-small-regular">
          Generate it from Compliance Calendar to see filings and deadlines
          across all your entities here.
        </span>
      </>
    );
  }
  if (mode === 'stacked') {
    return (
      <>
        <span className="txt-medium-bold">Nothing due in the next 30 days</span>
        <span className="txt-small-regular">
          Filings will appear here as their due dates come within range.
        </span>
      </>
    );
  }
  return (
    <>
      <span className="txt-medium-bold">No deadlines this month</span>
      <span className="txt-small-regular">
        No filings or deadlines across any of your entities this month.
      </span>
    </>
  );
};

const CalendarAgendaView = ({
  eventsByDate,
  currentMonth,
  onSelectEvent,
  mode = 'horizontal',
  calendarNotGenerated = false,
  viewStart,
  viewEnd,
  header,
  onFilingMutated,
  className,
}: CalendarAgendaViewProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showTopGradient, showBottomGradient } = useScrollGradient(scrollRef);

  const today = NonTimezoneSensitiveDate.now().toISO8601String();
  const monthStart = viewStart
    ? NonTimezoneSensitiveDate.fromISO8601String(viewStart)
    : NonTimezoneSensitiveDate.fromISO8601String(currentMonth).startOf('month');
  const monthEnd = viewEnd
    ? NonTimezoneSensitiveDate.fromISO8601String(viewEnd)
    : NonTimezoneSensitiveDate.fromISO8601String(currentMonth).endOf('month');

  const dateGroups = buildDateGroups(eventsByDate, monthStart, monthEnd);
  const contentPadding = mode === 'stacked' ? 'p-3' : 'p-6';
  const contentGap = mode === 'stacked' ? 'gap-3' : 'gap-6';

  return (
    <div
      className={cn(
        'flex flex-col flex-1 min-h-0 overflow-hidden bg-white rounded-lg border border-border-primary',
        header ? 'p-0 gap-0' : [contentPadding, contentGap],
        className
      )}
    >
      {header && (
        <div className="flex items-center justify-between px-xl py-lg border-b border-border-secondary shrink-0">
          {header}
        </div>
      )}
      <div className="relative flex flex-col flex-1 min-h-0">
        <div
          ref={scrollRef}
          className={cn(
            'flex flex-col flex-1 min-h-0 overflow-auto',
            contentPadding,
            contentGap
          )}
        >
          {dateGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 gap-1 text-center px-md">
              <EmptyState
                calendarNotGenerated={calendarNotGenerated}
                mode={mode}
              />
            </div>
          ) : (
            dateGroups.map(({ key, events }) => (
              <AgendaDateGroup
                key={key}
                date={key}
                events={events}
                isToday={key === today}
                mode={mode}
                onSelectEvent={onSelectEvent}
                onFilingMutated={onFilingMutated}
              />
            ))
          )}
        </div>
        {showTopGradient && (
          <div className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-white to-transparent" />
        )}
        {showBottomGradient && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
    </div>
  );
};

export default CalendarAgendaView;
